import React, { useState, useEffect, useRef, useMemo } from 'react';
import ContentCard from '../../../../../../components/layout/ContentCard/ContentCard';
import Table from '../../../../../../components/data/Table/Table';
import SearchFilter from '../../../../../../components/data/SearchFilter';
import Pagination from '../../../../../../components/data/Pagination';
import { ConfirmModal } from '../../../../../../components/common/ConfirmModal';
import { translate, getFormatDate } from '../../../../../../utils/Common';
import { usePlatforms } from '../../../../../../hooks/usePlatforms';
import { FaTrash } from 'react-icons/fa';

const DoorsTab = ({ company, showSuccess, showError }) => {
  const { getDoorsByCompanyCode, removeCompanyFromDoor } = usePlatforms();
  const [doors, setDoors] = useState([]);
  // Starts true: between the first render and the effect firing, a false
  // flag with an empty list paints the "nothing here" state for a frame,
  // which reads as the screen finding nothing and then changing its mind.
  const [doorsLoading, setDoorsLoading] = useState(true);
  const doorsAbortControllerRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [deletingDoorId, setDeletingDoorId] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [doorToDelete, setDoorToDelete] = useState(null);

  useEffect(() => {
    if (!company) return;
    
    // Usar companyCode directamente, no boxitCompanyCode
    const companyCode = company?.companyCode || company?.CodUser;
    if (!companyCode) {
      setDoors([]);
      return;
    }

    const loadDoors = async () => {
      setDoorsLoading(true);

      if (doorsAbortControllerRef.current) {
        doorsAbortControllerRef.current.abort();
      }

      const liveController = new AbortController();
      doorsAbortControllerRef.current = liveController;

      try {
        const result = await getDoorsByCompanyCode(
          companyCode,
          doorsAbortControllerRef.current.signal
        );

        const doorsList = Array.isArray(result) ? result : (result?.payload || []);
        setDoors(doorsList);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error loading doors:', err);
          if (showError) {
            showError(err.message || translate('dashboard.companies.detail.doors.errors.loadError'));
          }
          setDoors([]);
        }
      } finally {
        // Only the request that was not superseded may clear the flag: an
        // aborted one used to switch it off for its replacement, blanking the
        // view until the new data arrived.
        if (!liveController.signal.aborted) setDoorsLoading(false);
      }
    };

    loadDoors();

    return () => {
      if (doorsAbortControllerRef.current) {
        doorsAbortControllerRef.current.abort();
      }
    };
  }, [company, getDoorsByCompanyCode, removeCompanyFromDoor]);

  // Filtrar doors basado en búsqueda y filtros
  const filteredDoors = useMemo(() => {
    let result = [...doors];

    // Aplicar búsqueda general
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(door => {
        const doorNumber = (door.doorNumber || door.doorId || '').toString().toLowerCase();
        const locationName = (door.locationName || '').toLowerCase();
        const doorId = (door.doorId || '').toString().toLowerCase();
        
        return doorNumber.includes(searchLower) ||
               locationName.includes(searchLower) ||
               doorId.includes(searchLower);
      });
    }

    // Aplicar filtros específicos
    if (filters.doorNumber) {
      result = result.filter(door =>
        (door.doorNumber || door.doorId || '').toString().toLowerCase().includes(filters.doorNumber.toLowerCase())
      );
    }

    if (filters.locationName) {
      result = result.filter(door =>
        (door.locationName || '').toLowerCase().includes(filters.locationName.toLowerCase())
      );
    }

    return result;
  }, [doors, searchTerm, filters]);

  // Paginación
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDoors = filteredDoors.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredDoors.length / itemsPerPage);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleFilter = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const handleRemoveDoorClick = (door) => {
    setDoorToDelete(door);
    setConfirmModalOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!company || !doorToDelete) return;
    
    const companyCode = company?.companyCode || company?.CodUser;
    if (!companyCode) {
      if (showError) {
        showError(translate('dashboard.companies.detail.errors.companyCodeNotFound'));
      }
      setConfirmModalOpen(false);
      setDoorToDelete(null);
      return;
    }

    const doorId = doorToDelete?.doorId || doorToDelete?.idPlataformaDet || doorToDelete?.IdPlataformaDet;
    if (!doorId) {
      if (showError) {
        showError(translate('dashboard.companies.detail.doors.errors.doorIdNotFound'));
      }
      setConfirmModalOpen(false);
      setDoorToDelete(null);
      return;
    }

    setDeletingDoorId(doorId);
    setConfirmModalOpen(false);

    try {
      const abortController = new AbortController();
      const result = await removeCompanyFromDoor(doorId, companyCode, abortController.signal);
      
      if (result && (result.code === 200 || result.message === 'COMPANY_WAS_REMOVED')) {
        // Recargar la lista de puertas
        const doorsResult = await getDoorsByCompanyCode(companyCode, abortController.signal);
        const doorsList = Array.isArray(doorsResult) ? doorsResult : (doorsResult?.payload || []);
        setDoors(doorsList);
        
        // Mostrar mensaje de éxito
        if (showSuccess) {
          showSuccess(
            translate('dashboard.companies.detail.doors.success.removeSuccess') || 
            `Puerta ${doorToDelete?.doorNumber || doorId} desasignada exitosamente`
          );
        }
      } else {
        if (showError) {
          showError(result?.message || translate('dashboard.companies.detail.doors.errors.removeError'));
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error removing door from company:', err);
        if (showError) {
          showError(err.message || translate('dashboard.companies.detail.doors.errors.removeError'));
        }
      }
    } finally {
      setDeletingDoorId(null);
      setDoorToDelete(null);
    }
  };

  const handleCancelRemove = () => {
    setConfirmModalOpen(false);
    setDoorToDelete(null);
  };

  const columns = [
    {
      key: 'doorId',
      label: translate('dashboard.companies.detail.doors.columns.doorNumber'),
      sortable: true,
      width: '150px',
      render: (row) => row?.doorNumber || '-'
    },
    {
      key: 'locationId',
      label: translate('dashboard.companies.detail.doors.columns.location'),
      sortable: true,
      width: '200px',
      render: (row) => row?.locationName || '-'
    },
    {
      key: 'createdAt',
      label: translate('dashboard.companies.detail.doors.columns.assignedAt'),
      sortable: true,
      width: '180px',
      render: (row) => row?.createdAt ? getFormatDate(row.createdAt) : '-'
    },
    {
      key: 'actions',
      label: '',
      width: '60px',
      sortable: false,
      render: (row) => {
        const doorId = row?.doorId || row?.idPlataformaDet || row?.IdPlataformaDet;
        const isDeleting = deletingDoorId === doorId;
        
        return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => handleRemoveDoorClick(row)}
              disabled={isDeleting}
              title={translate('dashboard.companies.detail.doors.removeTooltip') || 'Desasignar puerta'}
              style={{
                background: isDeleting ? 'var(--bg-disabled)' : 'var(--badge-error-bg)',
                color: isDeleting ? 'var(--text-disabled)' : 'var(--badge-error-text)',
                border: 'none',
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                cursor: isDeleting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                padding: 0
              }}
              onMouseEnter={(e) => {
                if (!isDeleting) {
                  e.currentTarget.style.background = 'var(--color-error)';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isDeleting) {
                  e.currentTarget.style.background = 'var(--badge-error-bg)';
                  e.currentTarget.style.color = 'var(--badge-error-text)';
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              <FaTrash size={12} style={{ opacity: isDeleting ? 0.5 : 1 }} />
            </button>
          </div>
        );
      }
    }
  ];

  return (
    <div>
      <SearchFilter
        placeholder={translate('dashboard.companies.detail.doors.filters.searchPlaceholder')}
        onSearch={handleSearch}
        filters={[
          { 
            type: 'text', 
            name: 'doorNumber', 
            placeholder: translate('dashboard.companies.detail.doors.filters.doorNumber')
          },
          { 
            type: 'text', 
            name: 'locationName', 
            placeholder: translate('dashboard.companies.detail.doors.filters.locationName')
          }
        ]}
        onFilter={handleFilter}
        activeFilters={filters}
      />

      <Table
        columns={columns}
        data={paginatedDoors}
        loading={doorsLoading}
        emptyMessage={translate('dashboard.companies.detail.doors.empty')}
        hoverable
      />

      {!doorsLoading && filteredDoors.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={filteredDoors.length}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModalOpen}
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        title={translate('dashboard.companies.detail.doors.confirmRemoveTitle') || 'Desasignar puerta'}
        message={
          doorToDelete 
            ? translate('dashboard.companies.detail.doors.confirmRemove', { 
                doorNumber: doorToDelete?.doorNumber || doorToDelete?.doorId || doorToDelete?.idPlataformaDet || ''
              }) || `¿Está seguro de que desea desasignar la puerta ${doorToDelete?.doorNumber || doorToDelete?.doorId || doorToDelete?.idPlataformaDet} de esta compañía?`
            : ''
        }
        confirmText={translate('dashboard.companies.detail.doors.confirmButton') || 'Desasignar'}
        cancelText={translate('global.cancel') || 'Cancelar'}
        variant="danger"
        isLoading={deletingDoorId !== null}
      />
    </div>
  );
};

export default DoorsTab;

